import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    Sprout, CalendarDays, MessageCircle, BookOpen, Upload,
    LayoutGrid, Home, Map, LogOut, User as UserIcon, Settings,
    Camera, Sparkles, CreditCard, Bug, Shield, ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import GardenSwitcher from "@/components/GardenSwitcher";
import InstallPrompt from "@/components/InstallPrompt";
import Logo from "@/components/Logo";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
    { to: "/", label: "Dashboard", icon: Home, end: true, testId: "nav-dashboard" },
    { to: "/map", label: "Garden Map", icon: Map, testId: "nav-map" },
    { to: "/beds", label: "Beds", icon: LayoutGrid, testId: "nav-beds" },
    { to: "/crops", label: "Crops", icon: Sprout, testId: "nav-crops" },
    { to: "/timeline", label: "Timeline", icon: CalendarDays, testId: "nav-timeline" },
    { to: "/images", label: "Photos", icon: Camera, testId: "nav-images" },
    { to: "/chat", label: "Mentor", icon: MessageCircle, testId: "nav-chat" },
    { to: "/pests", label: "Pests", icon: Bug, testId: "nav-pests" },
    { to: "/beneficials", label: "Beneficials", icon: Shield, testId: "nav-beneficials" },
    { to: "/book-notes", label: "Book Notes", icon: BookOpen, testId: "nav-book-notes" },
    { to: "/import", label: "Import CSV", icon: Upload, testId: "nav-import" },
];

function initials(name = "") {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join("") || "?";
}

function UserMenu({ compact = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    if (!user || user === false) return null;

    const isPlus = user.tier === "plus";

    const handleLogout = async () => {
        // Navigate FIRST so ProtectedRoute doesn't capture {from: /profile}
        // and bounce the user back to a protected page after re-login.
        navigate("/login", { replace: true });
        await logout();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="user-menu-trigger"
                    className={`flex items-center gap-3 w-full ${compact ? "px-2 py-1" : "px-3 py-2"} rounded-md hover:bg-[var(--vm-card)] transition`}
                >
                    <div className="relative shrink-0">
                        {user.avatar_url ? (
                            <img
                                src={user.avatar_url}
                                alt=""
                                data-testid="user-menu-avatar"
                                className="w-9 h-9 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-[var(--vm-secondary)] text-[var(--vm-bg)] flex items-center justify-center font-serif-display text-sm">
                                {initials(user.name)}
                            </div>
                        )}
                        {isPlus && (
                            <span
                                data-testid="plus-badge"
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--vm-accent)] text-[var(--vm-bg)] flex items-center justify-center shadow"
                                title="rooteed Plus"
                            >
                                <Sparkles className="w-2.5 h-2.5" strokeWidth={2.5} />
                            </span>
                        )}
                    </div>
                    {!compact && (
                        <div className="text-left min-w-0 flex-1">
                            <div className="text-sm text-[var(--vm-text)] truncate flex items-center gap-1">
                                {user.name}
                                {isPlus && <span className="text-[9px] uppercase tracking-wider text-[var(--vm-accent)]">Plus</span>}
                            </div>
                            <div className="text-[10px] text-[var(--vm-text-muted)] truncate">{user.email}</div>
                        </div>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">
                    Account
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="user-menu-profile">
                    <UserIcon className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="user-menu-billing">
                    <CreditCard className="w-4 h-4 mr-2" />
                    {isPlus ? "Manage Plus" : "Go Plus · €2"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="user-menu-settings">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                {user.role === "admin" && (
                    <>
                        <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">
                            <ShieldAlert className="w-4 h-4 mr-2" /> Admin · users
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/admin/stats")} data-testid="user-menu-admin-stats">
                            <ShieldAlert className="w-4 h-4 mr-2" /> Admin · stats
                        </DropdownMenuItem>
                    </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Layout() {
    const { user } = useAuth();

    return (
        <div className="min-h-screen paper-texture">
            <div className="app-shell flex">
                {/* Sidebar */}
                <aside className="hidden md:flex md:flex-col w-64 shrink-0 border-r border-[var(--vm-border)] min-h-screen p-6 sticky top-0 bg-[var(--vm-card)]/60 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <Logo variant="icon" size={44} />
                        <div>
                            <div className="font-serif-display text-2xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <GardenSwitcher />
                    </div>

                    <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
                        {links.map(l => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={l.testId}
                                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            >
                                <l.icon className="w-4 h-4" strokeWidth={1.5} />
                                <span>{l.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    <div className="mt-auto pt-6 border-t border-[var(--vm-border)]">
                        {user && user !== false && (
                            <div className="mb-3">
                                <UserMenu />
                            </div>
                        )}
                        <div className="text-xs text-[var(--vm-text-muted)] leading-relaxed px-3">
                            <div className="overline mb-1 text-[var(--vm-sage)]">Default climate</div>
                            {user?.location || "Ireland"} · {user?.climate_zone || "temperate maritime"}
                        </div>
                    </div>
                </aside>

                {/* Mobile top nav */}
                <header className="md:hidden fixed top-0 left-0 right-0 z-20 bg-[var(--vm-card)]/95 backdrop-blur-sm border-b border-[var(--vm-border)]">
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                            <Logo variant="icon" size={32} />
                            <span className="font-serif-display text-xl lowercase tracking-tight">rooteed</span>
                        </div>
                        {user && user !== false && <UserMenu compact />}
                    </div>
                    <div className="flex overflow-x-auto gap-1 px-3 pb-2">
                        {links.map(l => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`m-${l.testId}`}
                                className={({ isActive }) => `nav-link whitespace-nowrap text-sm py-2 px-3 ${isActive ? "active" : ""}`}
                            >
                                <l.icon className="w-4 h-4" strokeWidth={1.5} />
                                <span>{l.label}</span>
                            </NavLink>
                        ))}
                    </div>
                </header>

                {/* Main content */}
                <main className="flex-1 min-w-0 pt-32 md:pt-12 pb-16 px-6 md:px-12">
                    <div className="max-w-6xl mx-auto fade-up">
                        <Outlet />
                    </div>
                </main>
            </div>
            <InstallPrompt />
        </div>
    );
}
